import React, { useRef, useState } from 'react';
import { LinearProgress, Popover } from '@mui/material';
import useTranslate from '../../../../../customHooks/useTranslate';
import Button from '../../../../../muiComponents/Button';
import TextInput from '../../../../../muiComponents/TextInput/TextInput';
import { commonIcons } from '../../../../../utils/commonVariables/commonIcons';
import { ProviderServiceScraperPanel } from '../../../contexts/ServiceScraperPanelContext';
import { useZitoneoServices } from '../../../contexts/ZitoneoContentContext';
import { useZitoneoUI } from '../../../contexts/ZitoneoUIContext';
import useServiceScraping from '../../../hooks/useServiceScraping';
import ServiceScraperPanel from '../../presentation/serviceScraping/ServiceScraperPanel';
import UnsavedServicesChangesPopUp from '../header/UnsavedServicesChangesPopUp';

interface CategoryServiceScraperProps {
    categoryId: string;
}

const CategoryServiceScraperContent = ({
    categoryId,
}: CategoryServiceScraperProps) => {
    const translate = useTranslate();
    const { isLoading } = useZitoneoUI();
    const { hasUnsavedChanges, saveHotelContent } = useZitoneoServices();
    const { isAnalyzing, analyze } = useServiceScraping();
    const anchorRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isWarningOpen, setIsWarningOpen] = useState(false);
    const [scrapingUrl, setScrapingUrl] = useState('');

    const handleToggle = () => {
        if (hasUnsavedChanges) {
            setIsWarningOpen(true);
        } else {
            setIsOpen(previous => !previous);
        }
    };

    const handleAnalyze = async () => {
        if (hasUnsavedChanges) {
            setIsWarningOpen(true);
        } else {
            await analyze(scrapingUrl, [categoryId]);
            setIsOpen(false);
        }
    };

    return (
        <>
            <div ref={anchorRef}>
                <Button
                    type='button'
                    variant='outlined'
                    startIcon={commonIcons.AI}
                    onClick={handleToggle}
                    disabled={isLoading || isAnalyzing}
                    aria-haspopup='dialog'
                    aria-expanded={isOpen}
                    aria-controls='category-service-scraper'
                    size='small'
                >
                    {translate('autoimport_with_ai')}
                </Button>
            </div>
            <Popover
                id='category-service-scraper'
                open={isOpen}
                anchorEl={anchorRef.current}
                onClose={() => {
                    if (!isAnalyzing) {
                        setIsOpen(false);
                    }
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <div
                    className='category-service-scraper'
                    aria-busy={isAnalyzing}
                >
                    {isAnalyzing ? (
                        <div role='status'>
                            <p>{translate('analyzing_hold')}</p>
                            <LinearProgress
                                aria-label={translate('analyzing_hold')}
                            />
                        </div>
                    ) : null}
                    <TextInput
                        id='category-service-scraping-url'
                        labelText='website_to_search_optional'
                        placeholder={translate('website_placeholder')}
                        value={scrapingUrl}
                        handleChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                        ) => setScrapingUrl(event.target.value)}
                        disabled={isAnalyzing}
                        helperText='restrict_ai_search_to_website'
                    />
                    <Button
                        type='button'
                        variant='contained'
                        startIcon={commonIcons.AI}
                        onClick={handleAnalyze}
                        disabled={isAnalyzing}
                    >
                        {translate('analyze')}
                    </Button>
                </div>
            </Popover>
            <UnsavedServicesChangesPopUp
                isOpen={isWarningOpen}
                onClose={() => setIsWarningOpen(false)}
                onSave={async () => {
                    await saveHotelContent();
                    setIsOpen(true);
                }}
            />
            <ServiceScraperPanel />
        </>
    );
};

/**
 * Button that unfolds a web scraping form to auto-import the services of a
 * category and opens the scraper review panel with the result.
 * @param props - The category to analyze.
 * @returns The category scraper control with its review panel.
 */
const CategoryServiceScraper = ({
    categoryId,
}: CategoryServiceScraperProps) => (
    <ProviderServiceScraperPanel>
        <CategoryServiceScraperContent categoryId={categoryId} />
    </ProviderServiceScraperPanel>
);

export default CategoryServiceScraper;
